import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Stack, Alert, Group, Text, Box, ActionIcon, Loader } from "@mantine/core";
import { IconUpload, IconCheck, IconAlertCircle, IconFile, IconTrash, IconEye } from "@tabler/icons-react";
import { useDropzone } from "react-dropzone";

import { useUploadDocument } from "@hooks/mutations/onboarding/useUploadDocument";
import { useSubmitSession } from "@hooks/mutations/onboarding/useSubmitSession";
import { useUpdateMySession } from "@hooks/mutations/onboarding/useUpdateMySession";

import { imageFileTypes } from "@constants/fileTypes";
import { useAuth } from "@contexts/auth";

interface Step5Props {
  sessionData: any;
}

export const Step6Uploads = ({ sessionData }: Step5Props) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { mutate: uploadFile, isLoading: isUploading } = useUploadDocument();
  const { mutate: submitSession, isLoading: isSubmitting } = useSubmitSession();
  const { mutate: updateSession, isLoading: isUpdating } = useUpdateMySession();
  
  const [uploadingType, setUploadingType] = useState<string | null>(null);

  // Function to truncate file name for display
  const truncateFileName = (name: string) => {
    if (name.length > 30) {
      return name.substring(0, 15) + "..." + name.substring(name.length - 10);
    }
    return name;
  };

  const getFileUri = (fileName: string) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL as string;
    return fileName.startsWith("http") ? fileName : `${baseUrl}/v1/files/${fileName}?token=${token}`;
  };

  const handleFileUpload = (file: File, documentType: string) => {
    setUploadingType(documentType);
    uploadFile(
      { documentType, file },
      {
        onSettled: () => setUploadingType(null),
      }
    );
  };

  const handleSubmit = () => {
    updateSession({ current_step: 7 });
  };

  const handleBack = () => {
    updateSession({ current_step: 5 });
  };

  // Custom generic dropzone hook wrapper
  const FileUploader = ({ label, documentType, url, required }: { label: string, documentType: string, url: string | null, required: boolean }) => {
    const isUploaded = !!url;
    const isCurrentlyUploading = isUploading && uploadingType === documentType;

    const onDropAccepted = useCallback((acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleFileUpload(acceptedFiles[0], documentType);
      }
    }, []);

    const handleRemoveFile = () => {
      updateSession({ [`${documentType}_url`]: "" });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { "image/*": imageFileTypes, "application/pdf": [".pdf"] },
      onDrop: onDropAccepted,
      multiple: false,
      maxFiles: 1,
      maxSize: 5 * 1024 * 1024, // 5MB
      disabled: isCurrentlyUploading || isUploaded,
    });

    return (
      <Box mb="md">
        <Text size="sm" fw={500} mb="xs">
          {label} {required && <Text span c="red">*</Text>}
        </Text>
        
        {isUploaded ? (
          <div className="file-preview-container">
            <div className="file-info">
              <IconCheck size={20} color="green" className="file-icon" />
              <div className="file-details">
                <div className="file-name">Successfully uploaded</div>
                <div className="file-size">Stored securely</div>
              </div>
            </div>
            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                color="blue"
                component="a"
                href={getFileUri(url!)}
                target="_blank"
                title="View Document"
              >                <IconEye size={16} />
              </ActionIcon>
              <ActionIcon 
                variant="subtle" 
                color="red" 
                onClick={handleRemoveFile} 
                loading={isUpdating}
                title="Remove Document"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </div>
        ) : isCurrentlyUploading ? (
          <div className="file-preview-container">
            <div className="file-info">
              <Loader size="sm" color="blue" className="file-icon" />
              <div className="file-details">
                <div className="file-name">Uploading...</div>
                <div className="file-size">Please wait</div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`file-upload-container ${isDragActive ? "active" : ""}`} {...getRootProps()}>
            <input {...getInputProps()} />
            <Text size="sm" className="upload-text">
              Browse files to upload
            </Text>
            <Text size="sm" className="upload-info">
              PNG, JPG, or PDF up to 5MB
            </Text>
            <Button
              variant="outline"
              className="upload-button"
              leftSection={<IconUpload size={16} className="upload-icon" />}
            >
              Browse files
            </Button>
          </div>
        )}
      </Box>
    );
  };

  const isReadyToSubmit = sessionData.profile_photo_url && sessionData.signature_url;

  return (
    <Stack gap="lg">
      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        {t("onboarding-upload-docs-desc")}
      </Alert>

      <FileUploader label={t("onboarding-profile-photo")} documentType="profile_photo" url={sessionData.profile_photo_url} required={true} />
      <FileUploader label={t("onboarding-medical-license")} documentType="medical_license" url={sessionData.medical_license_url} required={false} />
      <FileUploader label={t("onboarding-proof-qual")} documentType="proof_of_qualification" url={sessionData.proof_of_qualification_url} required={false} />
      <FileUploader label={t("onboarding-signature-img")} documentType="signature" url={sessionData.signature_url} required={true} />

      <Group justify="space-between" mt="xl">
        <Button variant="outline" onClick={handleBack} loading={isUpdating} type="button">
          {t("onboarding-back")}
        </Button>
        <Button 
          size="md" 
          onClick={handleSubmit} 
          loading={isSubmitting}
          disabled={!isReadyToSubmit}
        >
          {t("onboarding-continue-contract")}
        </Button>
      </Group>
    </Stack>
  );
};
